import { useState, useSyncExternalStore } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { formatearHora } from '@/lib/fecha'
import { cerrarSesion, sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import { useSalirDelSoporte, useSoporteDelServidor } from '@/dominio/consola/soporte'
import { traducirErrorConsola, type ErrorTraducido } from '@/dominio/consola/useConsola'

/**
 * La franja del modo soporte en el portal (`docs/09` §4 y §6).
 *
 * Fija, arriba, sin botón de cerrar. Mientras un operador está dentro de la
 * cuenta de un cliente tiene que verlo en todo momento: lo que haga en `actuar`
 * queda en la auditoría **del cliente** con su nombre.
 *
 * Aparece si el servidor dice que hay soporte **o** si la sesión guardada lo
 * dice. El servidor es quien manda sobre el modo y la hora de cierre; la marca
 * local hace que la franja esté desde el primer pintado, sin esperar a la red,
 * y que siga estando si `GET /interna/sesion/actual` todavía no trae el campo.
 * Quedarse sin franja dentro de la cuenta de un cliente es el fallo caro; una
 * franja de más no le cuesta nada a nadie.
 */
export function FranjaSoporte() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const servidor = useSoporteDelServidor(sesion)
  const { cerrarEnElServidor, olvidarEnEstaPestana } = useSalirDelSoporte()
  const navegar = useNavigate()
  const [saliendo, setSaliendo] = useState(false)
  const [error, setError] = useState<ErrorTraducido | null>(null)

  const soporte = servidor.data ?? null
  const local = sesion?.soporte
  // La sesión de un despacho no lleva contribuyente, y el soporte siempre entra a uno.
  if (sesion === null || sesion.activo === null || (soporte === null && local === undefined)) return null

  const modo = soporte?.modo ?? local?.modo ?? 'mirar'
  const tenantId = sesion.activo.tenant

  const volverALaConsola = async (): Promise<void> => {
    // Primero se sale del portal y después se suelta el testigo: al revés, la
    // raíz pintaría la entrada del portal un instante antes de navegar.
    await navegar({ to: '/consola/clientes/$id', params: { id: tenantId }, search: { pestana: 'soporte' } })
    olvidarEnEstaPestana()
    cerrarSesion()
  }

  const salir = async (): Promise<void> => {
    setSaliendo(true)
    setError(null)
    try {
      if (local !== undefined) await cerrarEnElServidor(local.sesionId)
      await volverALaConsola()
    } catch (fallo) {
      setError(traducirErrorConsola(fallo, 'cerrar la sesión de soporte'))
      setSaliendo(false)
    }
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-[var(--capa-barra)] flex min-h-8 shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-ambar px-4 py-1 text-white sin-impresion"
    >
      <span className="text-cuerpo">
        Estás en la cuenta de <strong className="font-semibold">{sesion.activo.razonSocial}</strong> como soporte (
        {modo})
        {modo === 'actuar' ? ' · lo que hagas queda en la auditoría del cliente' : ' · solo lectura'}
        {soporte?.expira ? (
          <>
            {' '}
            · hasta las <span className="mono">{formatearHora(soporte.expira).slice(0, 5)}</span>
          </>
        ) : null}
      </span>

      {error === null ? (
        <button
          type="button"
          onClick={() => void salir()}
          disabled={saliendo}
          className="rounded-md border border-white/70 px-2 text-etiqueta font-medium leading-6 hover:bg-white/15 disabled:opacity-60"
        >
          {saliendo ? 'Saliendo…' : 'Salir del soporte'}
        </button>
      ) : (
        <span className="flex flex-wrap items-center gap-2 text-etiqueta">
          <span>
            {error.titulo} La sesión de soporte caduca sola
            {soporte?.expira ? ` a las ${formatearHora(soporte.expira).slice(0, 5)}` : ''}.
          </span>
          <button
            type="button"
            onClick={() => void volverALaConsola()}
            className="rounded-md border border-white/70 px-2 font-medium leading-6 hover:bg-white/15"
          >
            Salir igualmente
          </button>
        </span>
      )}
    </div>
  )
}
